import {useMemo} from 'react'
import {useAppConfig} from '@/hooks/use-app-config'

export interface AttributeDefinitionOption {
    name: string
    displayName: string
    entityType: 'identity' | 'party' | 'identifier'
    /** Disambiguated display label — appends "(identity)" or "(party)" when name is shared */
    label: string
}

/**
 * Merges identity + party attribute definitions + identity-identifier type codes
 * from appConfig (no separate API calls). Identifier values are exposed to
 * outbound mappings under their lower-cased type code (e.g. `email`,
 * `upn`, `employee_no`) by OutboundConnectorTaskDataCache, so the source
 * picker has to advertise them alongside the attribute names.
 *
 * Produces a flat list with disambiguation labels for names that appear in
 * multiple entity types.
 */
export function useAttributeDefinitions(): AttributeDefinitionOption[] {
    const {identityAttributeDefinitions, partyAttributeDefinitions, identifierTypes} = useAppConfig()

    return useMemo(() => {
        // Find names that appear in both attribute lists (need disambiguation)
        const identityNames = new Set(identityAttributeDefinitions.map((d) => d.name))
        const partyNames = new Set(partyAttributeDefinitions.map((d) => d.name))
        const ambiguousNames = new Set(
            [...identityNames].filter((n) => partyNames.has(n)),
        )

        const identityOptions: AttributeDefinitionOption[] = identityAttributeDefinitions.map((d) => ({
            name: d.name,
            displayName: d.displayName,
            entityType: 'identity',
            label: ambiguousNames.has(d.name) ? `${d.displayName} (identity)` : d.displayName,
        }))

        const partyOptions: AttributeDefinitionOption[] = partyAttributeDefinitions.map((d) => ({
            name: d.name,
            displayName: d.displayName,
            entityType: 'party',
            label: ambiguousNames.has(d.name) ? `${d.displayName} (party)` : d.displayName,
        }))

        // WHY: identifier-type codes are exposed lower-cased so `source=email` resolves to the primary EMAIL identifier value at runtime.
        const identifierOptions: AttributeDefinitionOption[] = identifierTypes.map((t) => ({
            name: t.code.toLowerCase(),
            displayName: t.label,
            entityType: 'identifier',
            label: `${t.label} (identifier)`,
        }))

        return [...identityOptions, ...partyOptions, ...identifierOptions]
    }, [identityAttributeDefinitions, partyAttributeDefinitions, identifierTypes])
}
